/**
 * src/components/agent/FixTermSheet.jsx — Inline canonical-term correction
 * sheet for the voice overlay (2026-04-20 session 5).
 *
 * CEO report: "should be able to fix spelling quickly and update canonical
 * on the fly so it gets better quickly." Before this, every correction had
 * to flow through Settings → Vocabulary Review, which defeated on-the-fly
 * curation during a live voice session.
 *
 * Flow:
 *   1. CEO taps "Fix" in the voice overlay bottom bar.
 *   2. Sheet opens. "Heard" prefilled from the last user transcript (best-
 *      effort — the CEO can edit); "Should be" is empty.
 *   3. Tap Save → POST /api/ai action='voice.fix_term'.
 *   4. Server writes directly to domain_vocabulary + ChromaDB (trusted
 *      path, bypasses vocabulary_review_queue). Next voice turn picks it up.
 */

import { useState, useEffect, useRef } from 'react';
import { getAuthHeaders } from '../../lib/api/client';
import { logVoiceEvent } from '../../lib/voice/voiceLogger';
import { useT } from '../../lib/i18n/I18nContext';

const backdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.35)',
  zIndex: 10010,
};

const sheetStyle = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  background: 'var(--card)',
  color: 'var(--t1)',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  boxShadow: 'var(--she)',
  zIndex: 10011,
  padding: '16px 20px calc(24px + env(safe-area-inset-bottom))',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const titleStyle = {
  fontSize: 15,
  fontWeight: 700,
  color: 'var(--t1)',
  letterSpacing: 0.2,
};

const subtitleStyle = {
  fontSize: 12,
  color: 'var(--tm)',
  lineHeight: 1.5,
};

const labelStyle = {
  fontSize: 11,
  color: 'var(--tm)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  marginBottom: 4,
};

const inputStyle = {
  minHeight: 44,
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 15,
  border: '1px solid var(--bdr)',
  background: 'var(--glass)',
  color: 'var(--t1)',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const rowStyle = { display: 'flex', flexDirection: 'column', gap: 4 };
const actionsStyle = {
  display: 'flex',
  gap: 8,
  marginTop: 8,
  justifyContent: 'flex-end',
};

const cancelBtnStyle = {
  minHeight: 44,
  borderRadius: 12,
  padding: '0 16px',
  background: 'var(--glass)',
  color: 'var(--t1)',
  border: '1px solid var(--bdr)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const saveBtnStyle = (disabled) => ({
  minHeight: 44,
  borderRadius: 12,
  padding: '0 16px',
  background: disabled ? 'var(--glass)' : 'var(--navy-surface)',
  color: disabled ? 'var(--tm)' : 'var(--inv)',
  border: 'none',
  fontSize: 13,
  fontWeight: 700,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

const statusStyle = (kind) => ({
  fontSize: 12,
  color: kind === 'ok' ? 'var(--ok)' : kind === 'error' ? 'var(--bad)' : 'var(--tm)',
  minHeight: 16,
});

const categoryOptions = [
  { id: '', label: 'Auto' },
  { id: 'project', label: 'Project' },
  { id: 'marine_term', label: 'Boat / Vessel' },
  { id: 'person', label: 'Person / Client' },
  { id: 'supplier', label: 'Supplier' },
  { id: 'material', label: 'Material' },
  { id: 'custom', label: 'Other' },
];

export default function FixTermSheet({ open, onClose, initialHeard = '' }) {
  const { t } = useT();
  const [heard, setHeard] = useState('');
  const [correct, setCorrect] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState({ kind: 'idle', message: '' });
  const [saving, setSaving] = useState(false);
  const correctRef = useRef(null);

  // Reset state every open so a stale entry doesn't bleed across uses.
  useEffect(() => {
    if (open) {
      setHeard(String(initialHeard || '').slice(0, 120));
      setCorrect('');
      setCategory('');
      setStatus({ kind: 'idle', message: '' });
      setSaving(false);
      // Autofocus "Should be" — the CEO already knows what was heard; what
      // they need to type is the correct spelling.
      setTimeout(() => { correctRef.current?.focus?.(); }, 80);
    }
  }, [open, initialHeard]);

  if (!open) return null;

  const canSave = !saving && correct.trim().length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setStatus({ kind: 'idle', message: 'Saving…' });
    try {
      const auth = await getAuthHeaders();
      const resp = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({
          action: 'voice.fix_term',
          original: heard.trim(),
          corrected: correct.trim(),
          category: category || null,
          language: 'en',
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.ok === false) {
        const msg = data?.error || `Save failed (${resp.status})`;
        setStatus({ kind: 'error', message: msg });
        logVoiceEvent('voice_fix_term', 'error', msg, { heard, correct });
        setSaving(false);
        return;
      }
      logVoiceEvent('voice_fix_term', 'ok', `Canonical "${correct}" saved`, {
        heard: heard.slice(0, 60),
        correct: correct.slice(0, 60),
        merged: !!data?.merged,
        chroma_ok: !!data?.chromaOk,
      });
      // 90-07 AC-3 — clear saving state IMMEDIATELY so the button stops
      // reading 'Saving…' the moment the server responds. Previous code
      // left saving=true through the 900ms auto-close timer, which CEO
      // perceived as a stuck button (Puppeteer repro 2026-04-21 8:51 AM).
      setSaving(false);
      setStatus({
        kind: 'ok',
        message: data?.merged
          ? `Added "${heard}" → "${correct}" as an alias.`
          : `Saved "${correct}". Next voice turn will recognize it.`,
      });
      // 90-07 AC-3 — tightened auto-close from 900ms → 400ms. Enough
      // time to read the confirmation, fast enough to not feel stuck.
      setTimeout(() => { onClose?.(); }, 400);
    } catch (err) {
      const msg = err?.message || 'Network error';
      setStatus({ kind: 'error', message: msg });
      logVoiceEvent('voice_fix_term', 'error', msg);
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        style={{ ...backdropStyle, border: 'none', cursor: 'default', padding: 0 }}
        onClick={onClose}
        data-testid="fix-term-backdrop"
        aria-label={t('agentChat.fixTermClose')}
        tabIndex={-1}
      />
      <div
        style={sheetStyle}
        data-testid="fix-term-sheet"
        role="dialog"
        aria-label="Fix term"
      >
        <div style={titleStyle}>Fix term</div>
        <div style={subtitleStyle}>
          Teach STT a word it got wrong. Saves directly — no review queue.
          The next voice turn will catch it.
        </div>

        <div style={rowStyle}>
          <label style={labelStyle} htmlFor="fix-term-heard">{t('agentChat.fixTermHeardLabel')}</label>
          <input
            id="fix-term-heard"
            type="text"
            value={heard}
            onChange={(e) => setHeard(e.target.value)}
            placeholder="e.g. Muluk-m"
            style={inputStyle}
            data-testid="fix-term-heard-input"
            autoComplete="off"
          />
        </div>

        <div style={rowStyle}>
          <label style={labelStyle} htmlFor="fix-term-correct">Should be (canonical)</label>
          <input
            id="fix-term-correct"
            ref={correctRef}
            type="text"
            value={correct}
            onChange={(e) => setCorrect(e.target.value)}
            placeholder="e.g. MULUC"
            style={inputStyle}
            data-testid="fix-term-correct-input"
            autoComplete="off"
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
          />
        </div>

        <div style={rowStyle}>
          <label style={labelStyle} htmlFor="fix-term-category">Category</label>
          <select
            id="fix-term-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
            data-testid="fix-term-category-select"
          >
            {categoryOptions.map((opt) => (
              <option key={opt.id || 'auto'} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div style={statusStyle(status.kind)} data-testid="fix-term-status">
          {status.message}
        </div>

        <div style={actionsStyle}>
          <button
            type="button"
            onClick={onClose}
            style={cancelBtnStyle}
            data-testid="fix-term-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            style={saveBtnStyle(!canSave)}
            data-testid="fix-term-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}
